import { useRef } from "react";
import { gsap, useGSAP, MOTION_OK } from "../gsap";
import { Frame } from "./Frame";
import { handcraftWorks, type Piece } from "../content";

const columns = [
  handcraftWorks.filter((_, i) => i % 2 === 0),
  handcraftWorks.filter((_, i) => i % 2 === 1),
];

function Item({ piece }: { piece: Piece }) {
  return (
    <figure className="craft-piece">
      <Frame piece={piece} />
      <figcaption className="mt-3 flex flex-col gap-0.5 px-1 sm:mt-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4 sm:px-1.5">
        <span className="font-display text-xs font-bold uppercase leading-tight tracking-tight sm:text-base">{piece.title}</span>
        <span className="text-xs text-smoke sm:text-sm">{piece.medium}</span>
      </figcaption>
    </figure>
  );
}

export function Handcraft() {
  const root = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        // Each object grows into place as it arrives, then holds still.
        gsap.utils.toArray<HTMLElement>(".craft-piece").forEach((piece) => {
          gsap.from(piece, {
            scale: 0.84,
            opacity: 0.3,
            transformOrigin: "50% 100%",
            ease: "none",
            scrollTrigger: { trigger: piece, start: "top 98%", end: "top 60%", scrub: true },
          });
        });
      });
    },
    { scope: root },
  );

  return (
    <section ref={root} id="handcraft" className="px-4 py-20 md:px-8 md:py-48">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 md:grid-cols-12 md:gap-8">
        <div className="md:col-span-4">
          <div className="md:sticky md:top-32">
            <h2 className="font-display text-[clamp(2.75rem,4.6vw,5rem)] font-extrabold uppercase leading-[0.88] tracking-[-0.04em]">
              Made <br />
              by hand
            </h2>
            <p className="mt-8 max-w-[36ch] text-base leading-relaxed text-smoke">
              Dice pouches with scale flaps, leather trays, chainmail jewellery and candles. Each one made by hand,
              one at a time.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-6 md:col-span-8 md:gap-10">
          <div className="flex flex-col gap-8 sm:gap-12 md:gap-20">
            {columns[0]!.map((piece) => (
              <Item key={piece.title} piece={piece} />
            ))}
          </div>
          <div className="flex flex-col gap-8 pt-12 sm:gap-12 sm:pt-32 md:gap-20">
            {columns[1]!.map((piece) => (
              <Item key={piece.title} piece={piece} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
